import React, { useState, useEffect } from 'react';
import { Shield, Eye, EyeOff, CheckCircle, XCircle, Loader } from 'lucide-react';
import { validateToken, activateAccount } from '../../api/invitations';

export default function ActivateAccount() {
  const token = new URLSearchParams(window.location.search).get('token');

  const [step, setStep]       = useState('loading'); // loading | valid | invalid | success
  const [info, setInfo]       = useState(null);
  const [errMsg, setErrMsg]   = useState('');
  const [form, setForm]       = useState({ username: '', password: '', confirm: '' });
  const [showPw, setShowPw]   = useState(false);
  const [saving, setSaving]   = useState(false);
  const [errors, setErrors]   = useState({});

  useEffect(() => {
    if (!token) { setStep('invalid'); setErrMsg('Enlace de invitación no válido.'); return; }
    validateToken(token)
      .then(data => { setInfo(data); setStep('valid'); })
      .catch(err => {
        setErrMsg(err.response?.data?.error || 'Enlace inválido o expirado.');
        setStep('invalid');
      });
  }, [token]);

  const validate = () => {
    const e = {};
    if (!form.username.trim())      e.username = 'El nombre de usuario es requerido';
    if (form.username.includes(' '))e.username = 'El username no puede tener espacios';
    if (form.password.length < 8)   e.password = 'Mínimo 8 caracteres';
    if (!/[A-Z]/.test(form.password))    e.password = 'Debe tener al menos una mayúscula';
    if (!/[0-9]/.test(form.password))    e.password = 'Debe tener al menos un número';
    if (form.password !== form.confirm)  e.confirm  = 'Las contraseñas no coinciden';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    setErrors({});
    try {
      await activateAccount({ token, username: form.username.trim(), password: form.password });
      setStep('success');
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || 'Error al activar la cuenta' });
    } finally {
      setSaving(false);
    }
  };

  const goToLogin = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">7Family HR</h1>
          <p className="text-white/70 text-sm mt-1">Sistema de Gestión de Recursos Humanos</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">

          {/* ─── Loading ─── */}
          {step === 'loading' && (
            <div className="p-10 text-center">
              <Loader className="w-10 h-10 text-indigo-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Verificando enlace de invitación...</p>
            </div>
          )}

          {/* ─── Inválido / Expirado ─── */}
          {step === 'invalid' && (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-9 h-9 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Enlace no válido</h2>
              <p className="text-gray-500 text-sm mb-6">{errMsg}</p>
              <p className="text-xs text-gray-400">
                Contacta a tu administrador para solicitar una nueva invitación.
              </p>
              <button
                onClick={goToLogin}
                className="mt-6 px-6 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Ir al inicio de sesión
              </button>
            </div>
          )}

          {/* ─── Formulario ─── */}
          {step === 'valid' && (
            <>
              <div className="bg-indigo-50 px-6 py-5 border-b border-indigo-100">
                <h2 className="text-lg font-bold text-gray-800">Activa tu cuenta</h2>
                {info && (
                  <p className="text-sm text-gray-500 mt-1">
                    Hola <span className="font-semibold text-indigo-700">{info.employee_nombre}</span>,
                    bienvenido a <span className="font-semibold">{info.company_nombre}</span>
                  </p>
                )}
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Email (solo lectura) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    value={info?.email || ''}
                    readOnly
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>

                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de usuario *
                  </label>
                  <input
                    type="text"
                    value={form.username}
                    onChange={e => setForm(f => ({ ...f, username: e.target.value.toLowerCase().replace(/\s/g, '') }))}
                    placeholder="ej: juan.perez"
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 ${
                      errors.username ? 'border-red-400' : 'border-gray-200'
                    }`}
                  />
                  {errors.username && <p className="text-xs text-red-500 mt-1">{errors.username}</p>}
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contraseña *
                  </label>
                  <div className="relative">
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                      placeholder="Mínimo 8 caracteres, 1 mayúscula y 1 número"
                      className={`w-full border rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 ${
                        errors.password ? 'border-red-400' : 'border-gray-200'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
                </div>

                {/* Confirm password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmar contraseña *
                  </label>
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={form.confirm}
                    onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                    placeholder="Repite la contraseña"
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 ${
                      errors.confirm ? 'border-red-400' : 'border-gray-200'
                    }`}
                  />
                  {errors.confirm && <p className="text-xs text-red-500 mt-1">{errors.confirm}</p>}
                </div>

                {errors.submit && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    {errors.submit}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={saving}
                  className="w-full py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors mt-2"
                >
                  {saving ? 'Activando cuenta...' : 'Activar mi cuenta'}
                </button>
              </form>
            </>
          )}

          {/* ─── Éxito ─── */}
          {step === 'success' && (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-9 h-9 text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">¡Cuenta activada!</h2>
              <p className="text-gray-500 text-sm mb-2">
                Tu cuenta ha sido creada correctamente.
              </p>
              <p className="text-gray-500 text-sm mb-6">
                Usuario: <span className="font-bold text-indigo-600">{form.username}</span>
              </p>
              <button
                onClick={goToLogin}
                className="px-6 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Iniciar sesión
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-white/50 text-xs mt-6">
          © {new Date().getFullYear()} 7Family HR. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
}
